import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, filter, Subject, Subscription, switchMap } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { M3PayorAllocateService } from './m3-payor-allocate.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-m3-payor-allocate',
    templateUrl: './m3-payor-allocate.component.html',
    styleUrls: ['./m3-payor-allocate.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class M3PayorAllocateComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private isInitialized = false;
  private isLoading = false;

  baseURLs: any = {};
  filterParams: any = {};
  rowData: any = {};
  gridData: any[] = [];
  globalService: any;
  GENERAL_CONFIG: any;
  USER_SETTINGS: any;
  SERVER_CONFIG: any;

  currentTabName = 'Form Entry';
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;

  title = 'Payor Allocation';
  activeTab = 'payorAllocation';
  loadingAccounts = false;
  entityData: any[] = [];
  entityOriginalData: any[] = [];
  payorAccountData: any[] = [];
  thirdPartyData: any[] = [];
  isThirdPartyEnabled = 'N';
  isAllocateAccess = false;

  paymentType = '';
  classificationType = '';
  transType = 'BOOK_AMT';
  leKeys = '';
  loggedInUser = '';
  taxYear = '';
  scenario = '';
  jcdKey = '';

  payorName = '';
  payorEin = '';

  M3PayorTypes = [
    { PAYOR_TYPE: 'Foreign Corp', TYPE: 'FC' },
    { PAYOR_TYPE: 'Domestic Corp', TYPE: 'DC' },
    { PAYOR_TYPE: 'Domestic Partnership', TYPE: 'DP' },
    { PAYOR_TYPE: 'Foreign Partnership', TYPE: 'FP' }
  ];

  tpSearchResults: any[] = [];
  tpSearchLoading = false;

  activeTpField: any = null;
    private tpSearchSubject = new Subject<{ line: string; searchVal: string }>();
    private tpSearchSubscription: Subscription | null = null;

  constructor(
    private m3PayorAllocateService: M3PayorAllocateService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.isInitialized = false;
        this.isLoading = false;
        this.applyWorkspaceContext();
        this.tryInit();
        this.cdr.markForCheck();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.isInitialized = false;
        this.isLoading = false;
        this.applyWorkspaceContext();
        this.tryInit();
        this.cdr.markForCheck();
      })
    );
	this.tpSearchSubscription = this.tpSearchSubject.pipe(
	      debounceTime(150),
	      filter(({ searchVal }) => !!searchVal && searchVal.length >= 3),
	      switchMap(({ line, searchVal }) => {
	        this.tpSearchLoading = true;
	        this.cdr.markForCheck();
	        return this.m3PayorAllocateService.getTradingPartners(line, searchVal, this.globalService?.globalParams);
	      })
	    ).subscribe(
	      (data: any) => {
	        this.tpSearchResults = data || [];
	        this.tpSearchLoading = false;
	        this.cdr.markForCheck();
	      },
	      (error: any) => {
	        console.error('Trading Partner search error:', error);
	        this.tpSearchResults = [];
	        this.tpSearchLoading = false;
	        this.cdr.markForCheck();
	      }
	    );
    this.tryInit();
  }

  ngOnDestroy(): void {
	if (this.tpSearchSubscription) {
	  this.tpSearchSubscription.unsubscribe();
	}
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow) ? (rawRow[0] && typeof rawRow[0] === 'object' ? rawRow[0] : {}) : (rawRow || {});
    const rawGrid = gridPayloadFromActiveScreen(activeScreen);
    this.gridData = Array.isArray(rawGrid) ? rawGrid : [];
    this.globalService = {
      ...header,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
    };
    if (this.GENERAL_CONFIG?.base_url) {
      this.m3PayorAllocateService.setBaseUrl(this.GENERAL_CONFIG.base_url);
    } else if (this.baseURLs?.api) {
      this.m3PayorAllocateService.setBaseUrl(this.baseURLs.api);
    }
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) return;
    const gp = this.globalService?.globalParams || {};
    if (!gp || !this.GENERAL_CONFIG || !this.gridData || this.gridData.length === 0) return;
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    this.isLoading = true;
    this.showFormLoader = true;

    this.paymentType = this.gridData[0]?.data?.PAYMENT_TYPE || '';
    this.classificationType = this.gridData[0]?.data?.CLASSIFICATION_TYPE || '';
    const gParams = this.globalService?.globalParams || {};
    this.taxYear = gParams.tax_year;
    this.scenario = gParams.scenario;
    this.jcdKey = gParams.jcd_key;
    this.loggedInUser = this.USER_SETTINGS?.user?.sso_id || '';

    this.loadThirdPartyPayorList();
    this.setSelectedParams();
    this.getPayorAccountData();
  }

  private loadThirdPartyPayorList(): void {
    this.m3PayorAllocateService.getThirdPartyPayorList(this.globalService?.globalParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.thirdPartyData = [];
        if (data.jsonObject) {
          data.jsonObject.forEach((element: any) => {
            this.thirdPartyData.push({
              PAYOR_NAME: element.PAYOR_EIN ? `${element.PAYOR_NAME}-${element.PAYOR_EIN}` : element.PAYOR_NAME
            });
          });
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error('M3PayorAllocate: Error loading third party payors', error);
      }
    );
  }

  private setSelectedParams(): void {
    const leKeyList: string[] = [];
    for (let i = 0; i < this.gridData.length; i++) {
      leKeyList.push(this.gridData[i].data.COMBINATION_KEY);
    }
    this.leKeys = leKeyList.toString();
  }

  private getPayorAccountData(): void {
    this.loadingAccounts = true;
    this.cdr.markForCheck();

    this.m3PayorAllocateService.getPayorAccountDetails(
      this.leKeys, this.paymentType, this.classificationType, this.transType, this.loggedInUser, this.globalService?.globalParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.loadingAccounts = false;
        this.payorAccountData = data.jsonObject || [];
        this.isThirdPartyEnabled = this.payorAccountData.length > 0 ? this.payorAccountData[0].IS_THIRDPARTY_PAYOR_ENABLED : 'N';
        this.setPayorModalData();
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.loadingAccounts = false;
        this.showFormLoader = false;
        this.isLoading = false;
        this._snackBar.open('Error loading payor account details', 'Close', { panelClass: 'error-snackbar', duration: 4000 });
        this.cdr.markForCheck();
      }
    );
  }

  private setPayorModalData(): void {
    this.isAllocateAccess = this.payorAccountData.some(
      (r: any) => String(r.IS_ALLOCATION_ACCESS) === 'Y'
    );

    const grouped = this.payorAccountData.reduce((a: any, b: any) => {
      a[b.CC_KEY] = a[b.CC_KEY] || [];
      a[b.CC_KEY].push(b);
      return a;
    }, {});

    const entityArr = Object.keys(grouped).map((k: string) => {
      const items = grouped[k];
      return {
        CC_KEY: items[0].CC_KEY,
        LEID: items[0].LEID,
        CDR_NO: items[0].CDR_NO,
        REP_PD: items[0].REPORTING_PERIOD,
        ME_CODE: items[0].ME_CODE,
        TP_ME_CODE: items[0].TP_ME_CODE || '',
        TOTAL_ALLOCATED_AMT: 0,
        UNALLOCATED_AMT: 0,
        LEDGER: items[0].LEDGER,
        AMT_TYPE: items[0].AMT_TYPE,
        URP_FLAG: items[0].URP_FLAG,
        ROW_TYPE: items[0].ROW_TYPE,
        Sys_Account_data: items
      };
    });

    this.entityData = entityArr;

    for (let i = 0; i < this.entityData.length; i++) {
      if (this.entityData[i].Sys_Account_data.length > 0 && this.entityData[i].Sys_Account_data[0].TO_ACCT_KEY != null) {
        const sysGrouped = this.entityData[i].Sys_Account_data.reduce((a: any, b: any) => {
          a[b.TO_ACCT_KEY] = a[b.TO_ACCT_KEY] || [];
          a[b.TO_ACCT_KEY].push(b);
          return a;
        }, {});

        const sysArr = Object.keys(sysGrouped).map((k: string) => {
          return {
            CC_KEY: sysGrouped[k][0].CC_KEY,
            LEID: sysGrouped[k][0].LEID,
            CDR_NO: sysGrouped[k][0].CDR_NO,
            REP_PD: sysGrouped[k][0].REPORTING_PERIOD,
            ME_CODE: sysGrouped[k][0].ME_CODE,
            TP_ME_CODE: sysGrouped[k][0].TP_ME_CODE || '',
            LEDGER: sysGrouped[k][0].LEDGER,
            AMT_TYPE: sysGrouped[k][0].AMT_TYPE,
            LINE: sysGrouped[k][0].LINE,
            IS_OPEN: false,
            TO_ACCT_KEY: sysGrouped[k][0].TO_ACCT_KEY,
            TO_ACCT: sysGrouped[k][0].TO_ACCT,
            TO_ACCT_DESC: sysGrouped[k][0].TO_ACCT_DESC,
            IS_VALIDATE: sysGrouped[k][0].IS_VALIDATE,
            SYS_BOOK_AMT: sysGrouped[k][0].SYS_BOOK_AMT,
            SYS_TAX_ADJ: sysGrouped[k][0].SYS_TAX_ADJ,
            SYS_TOTAL_AMT: sysGrouped[k][0].SYS_TOTAL_AMT,
            IS_SYS_TOTAL_ALLOCATED: sysGrouped[k][0].IS_SYS_TOTAL_ALLOCATED,
            ROW_TYPE: sysGrouped[k][0].ROW_TYPE,
            Account_data: sysGrouped[k]
          };
        });

        this.entityData[i].Sys_Account_data = JSON.parse(JSON.stringify(sysArr));
      }

      for (let j = 0; j < this.entityData[i].Sys_Account_data.length; j++) {
        if (this.entityData[i].Sys_Account_data[j].Account_data.length > 0 &&
            this.entityData[i].Sys_Account_data[j].Account_data[0].FROM_ACCT_KEY != null) {

          const acctGrouped = this.entityData[i].Sys_Account_data[j].Account_data.reduce((a: any, b: any) => {
            a[b.REDUCE_TYPE] = a[b.REDUCE_TYPE] || [];
            a[b.REDUCE_TYPE].push(b);
            return a;
          }, {});

          const acctArr = Object.keys(acctGrouped).map((k: string) => {
            const items = acctGrouped[k];
            const headerRow = JSON.parse(JSON.stringify(items[0]));
            headerRow.ROW_TYPE = 'HEADER_ROW';
            if (String(items[0].AMT_TYPE) === 'BOOK_AMT') {
              items.unshift(headerRow);
            }
            return {
              ROW_TYPE: items[0].ROW_TYPE,
              LEID: items[0].LEID,
              CDR_NO: items[0].CDR_NO,
              REP_PD: items[0].REPORTING_PERIOD,
              ME_CODE: items[0].ME_CODE,
              FROM_ACCT: items[0].FROM_ACCT,
              FROM_ACCT_DESC: items[0].FROM_ACCT_DESC,
              FROM_ACCT_KEY: items[0].FROM_ACCT_KEY,
              AMT_TYPE: items[0].AMT_TYPE,
              SCHDM_TEMPLATE_KEY: items[0].SCHDM_TEMPLATE_KEY,
              ADJ_ORIGIN_SOURCE: items[0].ADJ_ORIGIN_SOURCE,
              BOOK_AMT: items[0].BOOK_AMT,
              TOTAL_ALLOCATED_AMT: items[0].TOTAL_ALLOCATED_AMT,
              UNALLOCATED_AMT: items[0].UNALLOCATED_AMT,
              tp_dropdown_data: [],
              tpObject: { TP_LEID: '', TP_CDR_NO: '', TP_REP_PD: 0, TP_ME_CODE: '', TP_ENTITY: '' },
              tp_me_dropdown_data: [],
              TP_ME_CODE: items[0].TP_ME_CODE,
              sub_acct_data: items,
              DELETE_ROWS: []
            };
          });

          this.entityData[i].Sys_Account_data[j].Account_data = JSON.parse(JSON.stringify(acctArr));
        }
      }
    }
	this.buildTpDetailsForExistingRows();

    this.entityOriginalData = JSON.parse(JSON.stringify(this.entityData));
  }

  private buildTpDetailsForExistingRows(): void {
      for (const entity of this.entityData) {
        if (!entity.Sys_Account_data?.length) continue;
        for (const sysAcct of entity.Sys_Account_data) {
          if (!sysAcct.Account_data?.length) continue;
          for (const acct of sysAcct.Account_data) {
            if (!acct.sub_acct_data?.length) continue;
            for (const sub of acct.sub_acct_data) {
              const hasValidTpDetailsObject = sub.TP_DETAILS
                && typeof sub.TP_DETAILS === 'object'
                && sub.TP_DETAILS.TP_ENTITY;
              if ((sub.ROW_TYPE === 'TB' || sub.ROW_TYPE === 'EX') && !hasValidTpDetailsObject) {
                let displayEntity = sub.TP_ENTITY || '';
                if (!displayEntity && sub.TP_LEID) {
                  displayEntity = [sub.TP_LEID, sub.TP_CDR_NO, sub.TP_REP_PD, sub.TP_ME_CODE]
                    .filter(v => v != null && v !== '').join(' - ');
                }
                sub.TP_ENTITY = displayEntity;
                sub.TP_SEARCH = displayEntity;
                if (sub.TP_LEID != null) {
                  sub.TP_DETAILS = {
                    TP_LEID: sub.TP_LEID,
                    TP_CDR_NO: sub.TP_CDR_NO,
                    TP_REP_PD: sub.TP_REP_PD,
                    TP_ME_CODE: sub.TP_ME_CODE,
                    TP_ENTITY: displayEntity
                  };
                }
              }
              if (sub.IS_OTHER_PAYOR == null || sub.IS_OTHER_PAYOR === '') {
                const looksLikeThirdParty = (!sub.TP_LEID || sub.TP_LEID === '')
                  && (sub.PAYOR_NAME || sub.PAYOR_TYPE);
                sub.IS_OTHER_PAYOR = looksLikeThirdParty ? 'Y' : 'N';
              }
            }
          }
        }
      }
    }

  cancel(): void {
    if (JSON.stringify(this.entityData) === JSON.stringify(this.entityOriginalData)) {
      this.requestGridUpdate.emit('cancel');
    } else {
      if (this.crudLoading) {
        this._snackBar.open('The transaction is still in progress', 'Close', { panelClass: 'info-snackbar', duration: 4000 });
      } else if (confirm('Do you want to close Form without saving changes?')) {
        this.requestGridUpdate.emit('cancel');
      }
    }
  }

  save(): void {
    if (this.activeTab === 'setupPayor') {
      this.saveNewPayor();
    } else {
      this.savePayorAllocation();
    }
  }

  private savePayorAllocation(): void {
    if (this.crudLoading) {
      this._snackBar.open('Please wait while we save this request', 'Close', { panelClass: 'info-snackbar', duration: 4000 });
      return;
    }

    const entityFinalData = JSON.parse(JSON.stringify(this.entityData));
    if (JSON.stringify(entityFinalData) === JSON.stringify(this.entityOriginalData)) {
      this.cancel();
      return;
    }

    const returnValue = this.validateFormValues();
    if (returnValue === 1) {
      this.cdr.markForCheck();
      return;
    }

    const finalData: any[] = [];
    for (let x = 0; x < entityFinalData.length; x++) {
      const sysData = entityFinalData[x].Sys_Account_data;
      if (sysData?.length) {
        for (let w = 0; w < sysData.length; w++) {
          const acctData = sysData[w].Account_data;
          if (acctData?.length) {
            for (let j = 0; j < acctData.length; j++) {
              if (String(acctData[j].AMT_TYPE) === 'BOOK_AMT') {
                acctData[j].sub_acct_data.splice(0, 1);
              }
              const subData = acctData[j].sub_acct_data;
              if (subData?.length) {
                for (let k = 0; k < subData.length; k++) {
                  finalData.push(subData[k]);
                }
              }
              const deleteRows = acctData[j].DELETE_ROWS;
              if (deleteRows?.length) {
                for (let k = 0; k < deleteRows.length; k++) {
                  finalData.push(deleteRows[k]);
                }
              }
            }
          }
        }
      }
    }

    const clobSettings = [{ sso_id: this.loggedInUser, screen_key: this.globalService?.globalParams?.screen_key }];
    this.sendDetailsDB(finalData, clobSettings);
  }

  private sendDetailsDB(finalData: any[], clobSettings: any[]): void {
    this.crudLoading = true;
    this.cdr.markForCheck();

    this.m3PayorAllocateService.saveRPModal(finalData, clobSettings, this.classificationType, this.transType, this.globalService?.globalParams, this.filterParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.crudLoading = false;
        if (data.errorMessage && data.errorMessage !== 'null') {
          this._snackBar.open(data.errorMessage, 'Close', { panelClass: 'error-snackbar', duration: 4000 });
        } else {
          this._snackBar.open('Related Party Modal Data was Saved', 'Close', { panelClass: 'success-snackbar', duration: 4000 });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.crudLoading = false;
        this._snackBar.open('Error saving data', 'Close', { panelClass: 'error-snackbar', duration: 4000 });
        this.cdr.markForCheck();
      }
    );
  }

  saveNewPayor(): void {
    if (this.crudLoading) {
      this._snackBar.open('Please wait while we save this request', 'Close', { panelClass: 'info-snackbar', duration: 4000 });
      return;
    }
    if (this.payorName === '') {
      this._snackBar.open('Please enter the Payor Name', 'Close', { panelClass: 'error-snackbar', duration: 4000 });
      return;
    }
    const pattern = /\d{9}/;
    if (this.payorEin !== '' && !this.payorEin.match(pattern)) {
      this._snackBar.open('EIN should be a 9 digit number', 'Close', { panelClass: 'error-snackbar', duration: 4000 });
      return;
    }

    this.crudLoading = true;
    this.cdr.markForCheck();

    this.m3PayorAllocateService.saveThirdPartyPayor(this.payorName, this.payorEin, this.globalService?.globalParams, this.filterParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.crudLoading = false;
        if (data.errorMessage && data.errorMessage !== 'null') {
          this._snackBar.open(data.errorMessage, 'Close', { panelClass: 'error-snackbar', duration: 4000 });
        } else {
          this._snackBar.open('New Third Party Payor was Saved', 'Close', { panelClass: 'success-snackbar', duration: 4000 });
          this.payorName = '';
          this.payorEin = '';
          this.loadThirdPartyPayorList();
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.crudLoading = false;
        this._snackBar.open('Error saving third party payor', 'Close', { panelClass: 'error-snackbar', duration: 4000 });
        this.cdr.markForCheck();
      }
    );
  }

  allocatePayor(): void {
    const grouped = this.payorAccountData.reduce((a: any, b: any) => {
      a[b.CC_KEY] = a[b.CC_KEY] || [];
      a[b.CC_KEY].push(b);
      return a;
    }, {});

    const allocateData = Object.keys(grouped).map((k: string) => ({
      CC_KEY: grouped[k][0].CC_KEY,
      leid: grouped[k][0].LEID,
      cdr_number: grouped[k][0].CDR_NO,
      reporting_period: grouped[k][0].REPORTING_PERIOD,
      me_code: grouped[k][0].ME_CODE,
      LEDGER: grouped[k][0].LEDGER
    }));

    const clobSettings = [{ sso_id: this.loggedInUser, screen_key: this.globalService?.globalParams?.screen_key }];
    this.crudLoading = true;
    this.cdr.markForCheck();

    this.m3PayorAllocateService.allocateProcess(allocateData, clobSettings, this.globalService?.globalParams, this.filterParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.crudLoading = false;
        if (data.errorMessage && data.errorMessage !== 'null') {
          this._snackBar.open(data.errorMessage, 'Close', { panelClass: 'error-snackbar', duration: 4000 });
        } else {
          this._snackBar.open('Allocation process completed successfully', 'Close', { panelClass: 'success-snackbar', duration: 4000 });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.crudLoading = false;
        this._snackBar.open('Error during allocation process', 'Close', { panelClass: 'error-snackbar', duration: 4000 });
        this.cdr.markForCheck();
      }
    );
  }

  reset(): void {
    if (this.activeTab === 'setupPayor') {
      this.payorName = '';
      this.payorEin = '';
	} else {
	  this.entityData = JSON.parse(JSON.stringify(this.entityOriginalData));
	}
    this.cdr.markForCheck();
  }

  openSubDetails(mainIndex: number, sysacctIndex: number, flag: string): void {
    for (let i = 0; i < this.entityData.length; i++) {
      if (this.entityData[i].Sys_Account_data?.length > 0) {
        for (let j = 0; j < this.entityData[i].Sys_Account_data.length; j++) {
          this.entityData[i].Sys_Account_data[j].IS_OPEN = false;
        }
      }
    }
    if (flag === 'O') {
      if (this.entityData[mainIndex].Sys_Account_data?.length > 0) {
        this.entityData[mainIndex].Sys_Account_data[sysacctIndex].IS_OPEN = true;
      }
    }
    this.cdr.markForCheck();
  }

  addNewPayor(entityIndex: number, sysAcctIndex: number, acctIndex: number, subIndex: number): void {
    const newRow = JSON.parse(JSON.stringify(
      this.entityData[entityIndex].Sys_Account_data[sysAcctIndex].Account_data[acctIndex].sub_acct_data[subIndex]
    ));

    newRow.ROW_TYPE = 'NEW';
    newRow.IS_CHANGED = 'Y';
    newRow.ALLOCATED_AMT = 0;
    newRow.TP_DETAILS = '';
    newRow.TP_LEID = '';
    newRow.TP_CDR_NO = '';
    newRow.TP_REP_PD = '';
    newRow.TP_ME_CODE = '';
    newRow.TP_ENTITY = '';

    this.entityData[entityIndex].Sys_Account_data[sysAcctIndex].Account_data[acctIndex].sub_acct_data.push(newRow);
    this.cdr.markForCheck();
  }

  removePayor(entityIndex: number, sysAcctIndex: number, acctIndex: number, subIndex: number): void {
    const subAcct = this.entityData[entityIndex].Sys_Account_data[sysAcctIndex].Account_data[acctIndex].sub_acct_data[subIndex];
    if (subAcct.ROW_TYPE === 'EX') {
      subAcct.IS_CHANGED = 'D';
      this.entityData[entityIndex].Sys_Account_data[sysAcctIndex].Account_data[acctIndex].DELETE_ROWS.push(subAcct);
      this.entityData[entityIndex].Sys_Account_data[sysAcctIndex].Account_data[acctIndex].sub_acct_data.splice(subIndex, 1);
    } else if (subAcct.ROW_TYPE === 'NEW') {
      this.entityData[entityIndex].Sys_Account_data[sysAcctIndex].Account_data[acctIndex].sub_acct_data.splice(subIndex, 1);
    }
    this.updateAmount(entityIndex, sysAcctIndex, acctIndex, subIndex, 'R');
  }

  updateAmount(entityIndex: number, sysAcctIndex: number, acctIndex: number, subIndex: number, flag: string): void {
    let allocatedAmt = 0;
    const subAcctData = this.entityData[entityIndex].Sys_Account_data[sysAcctIndex].Account_data[acctIndex].sub_acct_data;
    for (let k = 1; k < subAcctData.length; k++) {
      if (subAcctData[k].TP_LEID != null && subAcctData[k].TP_LEID !== undefined) {
        allocatedAmt = allocatedAmt + subAcctData[k].ALLOCATED_AMT;
      }
    }
    subAcctData[0].TOTAL_ALLOCATED_AMT = allocatedAmt;
    subAcctData[0].UNALLOCATED_AMT = subAcctData[0].BOOK_AMT - subAcctData[0].TOTAL_ALLOCATED_AMT;
    this.cdr.markForCheck();
  }

  delayedClearResults(): void {
      setTimeout(() => {
        this.tpSearchResults = [];
        this.cdr.markForCheck();
      }, 200);
  }

  searchTradingPartners(line: string, searchVal: string, subAcct?: any): void {
    if (subAcct) {
      this.activeTpField = subAcct;
    }
    if (!searchVal || searchVal.length < 3) {
      this.tpSearchResults = [];
      return;
    }
	this.tpSearchSubject.next({ line, searchVal });
  }

  clearTpResults(): void {
      setTimeout(() => {
        this.tpSearchResults = [];
        this.activeTpField = null;
        this.cdr.markForCheck();
      }, 200);
  }

  setTradingPartner(tpDetails: any, entityIndex: number, sysAcctIndex: number, acctIndex: number, subIndex: number): void {
    const subAcct = this.entityData[entityIndex].Sys_Account_data[sysAcctIndex].Account_data[acctIndex].sub_acct_data[subIndex];
    subAcct.HAS_ERROR = 'N';
    subAcct.IS_CHANGED = 'Y';

    const isOtherSentinel = (tpDetails == null) ||
                            (tpDetails.IS_VALID != null && tpDetails.IS_VALID == 0) ||
                            (tpDetails.TP_KEY != null && tpDetails.TP_KEY == 0) ||
                            !tpDetails.TP_LEID;

    if (!isOtherSentinel) {
      subAcct.IS_OTHER_PAYOR = 'N';
      subAcct.TP_LEID = tpDetails.TP_LEID;
      subAcct.TP_CDR_NO = tpDetails.TP_CDR_NO;
      subAcct.TP_REP_PD = tpDetails.TP_REP_PD;
      subAcct.TP_ME_CODE = tpDetails.TP_ME_CODE;
      subAcct.TP_ENTITY = tpDetails.TP_ENTITY || '';
      if (String(subAcct.AMT_TYPE) === 'BOOK_AMT') {
        subAcct.TP_DETAILS = {
          TP_LEID: tpDetails.TP_LEID,
          TP_CDR_NO: tpDetails.TP_CDR_NO,
          TP_REP_PD: tpDetails.TP_REP_PD,
          TP_ME_CODE: tpDetails.TP_ME_CODE,
          TP_ENTITY: tpDetails.TP_ENTITY || ''
        };
      }
      if (String(subAcct.AMT_TYPE) === 'M_ADJUSTMENT' && subAcct.ROW_TYPE === 'EX') {
        subAcct.TP_SEARCH = tpDetails.TP_ENTITY || '';
      }
      if (String(subAcct.AMT_TYPE) === 'BOOK_AMT' && subAcct.ROW_TYPE === 'NEW') {
        subAcct.TP_LOAD = tpDetails.TP_ENTITY || '';
      }
    } else {
      subAcct.IS_OTHER_PAYOR = 'Y';
      subAcct.TP_LEID = null;
      subAcct.TP_CDR_NO = null;
      subAcct.TP_REP_PD = null;
      subAcct.TP_ME_CODE = null;
      subAcct.TP_ENTITY = tpDetails?.TP_ENTITY || subAcct.TP_ENTITY || '';
      if (String(subAcct.AMT_TYPE) === 'BOOK_AMT' && subAcct.ROW_TYPE === 'NEW') {
        subAcct.TP_LOAD = tpDetails?.TP_ENTITY || '';
      }
      if (String(subAcct.AMT_TYPE) === 'M_ADJUSTMENT' && subAcct.ROW_TYPE === 'EX') {
        subAcct.TP_SEARCH = tpDetails?.TP_ENTITY || '';
      }
    }
    this.tpSearchResults = [];
    this.activeTpField = null;
    this.cdr.markForCheck();
  }

  setPayorType(payorType: string, entityIndex: number, sysAcctIndex: number, acctIndex: number, subIndex: number): void {
    const subAcct = this.entityData[entityIndex].Sys_Account_data[sysAcctIndex].Account_data[acctIndex].sub_acct_data[subIndex];
    subAcct.IS_CHANGED = 'Y';
    if (payorType != null && payorType !== undefined) {
      subAcct.HAS_TYPE_ERROR = 'N';
      subAcct.PAYOR_TYPE = payorType;
    }
    this.cdr.markForCheck();
  }

  updatedDetails(entityIndex: number, sysAcctIndex: number, acctIndex: number, subIndex: number): void {
    this.entityData[entityIndex].Sys_Account_data[sysAcctIndex].Account_data[acctIndex].sub_acct_data[subIndex].IS_CHANGED = 'Y';
    this.cdr.markForCheck();
  }

  private validateFormValues(): number {
    let returnValue = 0;
    for (let i = 0; i < this.entityData.length; i++) {
      const sysData = this.entityData[i].Sys_Account_data;
      if (!sysData?.length) continue;
      for (let w = 0; w < sysData.length; w++) {
        const acctData = sysData[w].Account_data;
        if (!acctData?.length) continue;
        for (let j = 0; j < acctData.length; j++) {
          const subData = acctData[j].sub_acct_data;
          if (!subData?.length) continue;
          for (let k = 0; k < subData.length; k++) {
            if (String(subData[k].AMT_TYPE) === 'BOOK_AMT') {
              if (subData[k].ROW_TYPE === 'NEW') {
                if (subData[k].hasOwnProperty('IS_OTHER_PAYOR') && subData[k].IS_OTHER_PAYOR === 'Y') {
                  if (String(subData[k].LINE) === '1' && subData[k].PAYOR_TYPE == null) {
                    subData[k].HAS_TYPE_ERROR = 'Y';
                    subData[k].PAYOR_TYPE_ERROR = 'Please select the Payor Type';
                    sysData[w].IS_OPEN = true;
                    returnValue = 1;
                  }
                  if (subData[k].PAYOR_NAME == null) {
                    subData[k].HAS_NAME_ERROR = 'Y';
                    subData[k].PAYOR_NAME_ERROR = 'Please enter Payor Name';
                    sysData[w].IS_OPEN = true;
                    returnValue = 1;
                  }
                } else if (!subData[k].hasOwnProperty('TP_DETAILS') || subData[k].TP_DETAILS?.TP_LEID == null) {
                  subData[k].HAS_ERROR = 'Y';
                  subData[k].TP_ERROR = 'Please assign the Payor';
                  sysData[w].IS_OPEN = true;
                  returnValue = 1;
                }
              }
            } else {
              if (subData[k].hasOwnProperty('IS_OTHER_PAYOR') && subData[k].IS_OTHER_PAYOR === 'Y') {
                if (String(subData[k].LINE) === '1' && subData[k].PAYOR_TYPE == null) {
                  subData[k].HAS_TYPE_ERROR = 'Y';
                  subData[k].PAYOR_TYPE_ERROR = 'Please select the Payor Type';
                  sysData[w].IS_OPEN = true;
                  returnValue = 1;
                }
                if (subData[k].PAYOR_NAME == null) {
                  subData[k].HAS_NAME_ERROR = 'Y';
                  subData[k].PAYOR_NAME_ERROR = 'Please enter Payor Name';
                  sysData[w].IS_OPEN = true;
                  returnValue = 1;
                }
              }
            }
          }
        }
      }
    }
    return returnValue;
  }
}
